import type { Disruption } from '@common/types/Disruption'
import { mapOrd, rawOrd } from '@lc/libarray'
import {
	createEntityAdapter,
	createSlice,
	type WithSlice,
} from '@reduxjs/toolkit'
import { reducer } from './store'

export const disruptionsAdapter = createEntityAdapter({
	selectId: (disruption: Disruption) => disruption.id,
	sortComparer: mapOrd(rawOrd, disruption => disruption.id),
})

const { selectById, selectIds, selectAll } = disruptionsAdapter.getSelectors()

declare module './store' {
	// eslint-disable-next-line @typescript-eslint/no-empty-object-type
	export interface LoadedSlices extends WithSlice<typeof disruptionsSlice> {}
}

export type DisruptionsSlice = ReturnType<
	(typeof disruptionsSlice)['getInitialState']
>
export const disruptionsSlice = createSlice({
	name: 'disruptions',
	initialState: disruptionsAdapter.getInitialState(),
	reducers: {
		setDisruption: disruptionsAdapter.setOne,
		setDisruptions: disruptionsAdapter.setMany,
		clearDisruptions: disruptionsAdapter.removeAll,
	},
	selectors: {
		getDisruption: (state, id): Disruption | undefined =>
			selectById(state, id),
		listDisruptions: selectIds,
		getAllDisruptions: selectAll,
		getObjectDisruptions: (state, idObject): Disruption[] | undefined => {
			const findDisruption = Object.values(state.entities).filter(
				disruption =>
					disruption.impacted_objects.some(
						object => object.id === idObject,
					),
			)
			return findDisruption
		},
	},
})

export const {
	reducer: disruptionsReducer,
	actions: { setDisruption, setDisruptions, clearDisruptions },
	selectors: {
		getDisruption,
		listDisruptions,
		getAllDisruptions,
		getObjectDisruptions,
	},
} = disruptionsSlice.injectInto(reducer)
